import { Link } from 'react-router-dom';

import { LegalPage, P, Section, UL } from './LegalPage';

export function TermsPage() {
  return (
    <LegalPage
      title="Terms of Service"
      subtitle="The rules of the road for using Emailforge. Read carefully — using the product means you accept these terms."
      effectiveDate="April 25, 2026"
    >
      <Section title="Accounts and workspaces">
        <P>
          You can create one or more workspaces. Each workspace must have at least one owner; the owner is the legal
          entity responsible for the workspace's content and the recipients it sends to.
        </P>
        <P>
          You're responsible for keeping your credentials secure, for the actions of every member you invite, and for
          ensuring members have the right to send to the lists you upload.
        </P>
      </Section>

      <Section title="Acceptable use">
        <P>You agree not to use Emailforge to:</P>
        <UL>
          <li>Send unsolicited bulk email or any message that violates CAN-SPAM, CASL, GDPR, or local equivalents.</li>
          <li>Send phishing, malware, illegal content, or material that infringes intellectual property rights.</li>
          <li>Probe, scan, or otherwise test the vulnerability of our infrastructure without prior written consent.</li>
          <li>Use the product to harass, defame, or threaten any person.</li>
        </UL>
        <P>We reserve the right to suspend or terminate workspaces that violate this policy without prior notice.</P>
      </Section>

      <Section title="Plans and billing">
        <P>
          Free plans include the limits listed on <Link to="/pricing" style={{ color: 'var(--flame-2)' }}>pricing</Link>.
          Paid plans are billed monthly or annually, in advance, in the currency shown at checkout. Overages are billed
          on the next invoice; you can cap usage in workspace settings.
        </P>
        <P>
          You may cancel at any time from <span className="mono">Settings → Billing</span>. Refunds are pro-rated for
          annual plans within the first 30 days; otherwise we don't refund partial periods.
        </P>
      </Section>

      <Section title="Service availability">
        <P>
          We target 99.9% monthly uptime for the API and dashboard. Scheduled maintenance is announced at least 48
          hours in advance on the status page. For incidents, see{' '}
          <Link to="/status" style={{ color: 'var(--flame-2)' }}>/status</Link>.
        </P>
        <P>
          Email deliverability depends on the SMTP provider you connect, the reputation of your sending domain, and
          recipient mailbox provider behavior. Emailforge cannot guarantee delivery to any specific address.
        </P>
      </Section>

      <Section title="Your content">
        <P>
          You own all templates, brand assets, and recipient lists you upload. By using the service you grant us the
          minimum license needed to render, store, and dispatch them on your behalf — nothing more.
        </P>
        <P>
          You can export every template as <span className="mono">.tsx</span> at any time and run it on your own
          infrastructure. Lock-in is not a feature.
        </P>
      </Section>

      <Section title="Intellectual property">
        <P>
          The Emailforge brand, code, and design system remain ours. The MIT-licensed parts of the codebase (block
          renderer, schemas) are explicitly licensed for your use under those terms.
        </P>
      </Section>

      <Section title="Liability">
        <P>
          Emailforge is provided "as is". To the maximum extent permitted by law, our aggregate liability for any
          claim arising from the service is capped at the fees you paid us in the 12 months before the event giving
          rise to the claim.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          We may update these terms when the law, the product, or our infrastructure materially changes. We will
          email workspace owners at least 14 days before any change takes effect. Continued use after the effective
          date means you accept the new terms.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Legal: <a href="mailto:legal@emailforge.app" style={{ color: 'var(--flame-2)' }}>legal@emailforge.app</a>.
          For privacy-specific matters see the{' '}
          <Link to="/privacy" style={{ color: 'var(--flame-2)' }}>Privacy Policy</Link>.
        </P>
      </Section>
    </LegalPage>
  );
}
